import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';

const AVATAR_WOMAN = '/woman-head-icon.jpg';
const AVATAR_MAN = '/man-head-icon.jpg';

/** Edit `attribution` per card. Grid order: row1 → 4,5,7 | row2 → 9,1,2 | row3 → 3,6,8 (by `id`). */
const TESTIMONIAL_PLACEHOLDERS = [
  {
    id: 4,
    quote:
      'I would definitely recommend Harpwood House to anyone! I am happy here and have settled really well. I could not think of a bad word to say. I am very pleased with my room and my outdoor space - I have my own personal garden that I can use when I want to get outside - and with the staff that take care of me and the people I speak to front of house.',
    avatarSrc: AVATAR_MAN,
    attribution: 'Resident at Harpwood House',
  },
  {
    id: 5,
    quote:
      'I will be eternally grateful to the exceptional staff across all areas, laundry and cleaning teams to the skilled carers and nursing staff, for their support and care to my mum and our entire family. In my thirty years of professional life, I came into contact with many nursing homes that offered similar services to that of Harpwood House. I cannot think of even one that came up to the standards of compassion and care that Harpwood House staff delivered.',
    avatarSrc: AVATAR_WOMAN,
    attribution: 'Daughter of resident at Harpwood House',
  },
  {
    id: 7,
    quote: 'Viewing Harpwood House under such difficult times was made as easy as possible. The staff are very welcoming and all our questions were answered along with information that we did not ask but needed to know. Again moving in day was as seamless as possible, even allowing us to have lunch with my husband, who liked both his room and the view. Thank you Harpwood House for making a hard decision much easier.',
    avatarSrc: AVATAR_WOMAN,
    attribution: 'Wife of resident at Harpwood House',
  },
  {
    id: 9,
    quote: 'I would like to thank all the staff for their exceptional care and professionalism in looking after my mother who had severe dementia. Their dedication and sincere care was there to see every time I went to see her and I am so glad she was able to live her limited life in the comfort that she always received. Thank you to two staff members amongst the others - and another staff member in particular at the end - for easing the suffering both for my mother and my sister and I. From the bottom of my heart - thank you!',
    avatarSrc: AVATAR_MAN,
    attribution: 'Son of resident at Harpwood House',
  },
  {
    id: 1,
    quote:
      'When [Mum] was first discharged from hospital she had fragile health and a nasty pressure wound which we were told would never fully heal. The wonderful staff achieved this - so I can vouch for the standard of nursing care. The carers are a great bunch - I would say that someone popped their head in her door at least every 20 mins and when we were visiting we felt that nothing was too much trouble. Everyone was so friendly - management, nurses, carers, catering, housekeeping, maintenance, laundry - they were all happy to chat and answer questions.',
    avatarSrc: AVATAR_WOMAN,
    attribution: 'Daughter of resident at Harpwood House',
  },
  {
    id: 2,
    quote:
      'My mother has been at Harpwood House since September 2023. Moving into nursing care after having your own house and being independent is very difficult and as one might say a bitter pill to swallow. All the staff at Harpwood House have made this transition smooth and any small bumps, questions or concerns have been dealt with straight away and Mum has now become part of the family and is always encouraged to join in with any of the activities on offer but at the same time allowed space if she has a day that she wants to just be resting.',
    avatarSrc: AVATAR_WOMAN,
    attribution: 'Daughter of resident at Harpwood House',
  },
  {
    id: 3,
    quote:
      'My father moved here just before Christmas. He settled in straight away, in fact, better than the previous outstanding home he was at. From the start of the process of moving him and onward, we have felt well-supported as a family. The staff are brilliant with him and understand his routine and fit around him. He loves the open space and being able to roam around and chat with the staff. The garden is lovely and having a childrens play area is genius.',
    avatarSrc: AVATAR_WOMAN,
    attribution: 'Daughter of resident at Harpwood House',
  },
  {
    id: 6,
    quote:
      'My mother has been in Harpwood House for 7 weeks now and from the very first day the staff have been excellent. The food is also excellent and the carers never rush Mum when feeding her. All the facilities including rooms, dining room and gardens are all lovely and clean. We could not wish for a better home to look after Mum.',
    avatarSrc: AVATAR_MAN,
    attribution: 'Son of resident at Harpwood House',
  },
  {
    id: 8,
    quote: 'We have been visiting my aunt at Harpwood House for the past two months and always found staff from cleaners, carers and management helpful and very happy. Everyone always has a smile and a kind word. My aunt is happier than she has been for a while, content and relaxed and she loves all the carers and never said anything negative. Friends that visit have all been impressed and have all said how well she is being looked after.',
    avatarSrc: AVATAR_MAN,
    attribution: 'Nephew of resident at Harpwood House',
  },
];

function Testimonials() {
  return (
    <>
      <Helmet>
        <title>Testimonials from our residents and families</title>
        <meta
          name="description"
          content="Hear from our residents and families about their experiences at Harpwood House."
        />
        <link rel="canonical" href="https://www.harpwood.house/testimonials" />
      </Helmet>

      <div className="page">
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Header />

        <main id="main-content">
          <section className="content-section testimonials-page">
            <div className="testimonials-container">
              <h1>Testimonials</h1>
            </div>
          </section>

          <section className="content-section testimonials-intro">
            <div className="testimonials-intro__inner">
              <h2>Hear from our residents and families</h2>
              <p>
                At Harpwood House we are proud of the care we provide and the difference it makes to
                the lives of our residents and their families. Below you can read about the
                experiences of those who have chosen to make Harpwood House their home.
              </p>
              <p>
                If you would like to find out more or arrange a visit, please{' '}
                <Link to="/contact-us" className="nurse-care-link">
                  get in touch
                </Link>
                .
              </p>
            </div>
          </section>

          <section className="content-section testimonials-grid-section" aria-label="Written testimonials">
            <div className="testimonials-grid-wrap">
              <div className="testimonials-grid">
                {TESTIMONIAL_PLACEHOLDERS.map((item) => (
                  <article key={item.id} className="testimonial-card">
                    <div className="testimonial-card__avatar" aria-hidden="true">
                      <img src={item.avatarSrc} alt="" />
                    </div>
                    <span className="testimonial-card__mark testimonial-card__mark--open" aria-hidden="true">
                      &ldquo;
                    </span>
                    <p className="testimonial-card__text">{item.quote}</p>
                    <span className="testimonial-card__mark testimonial-card__mark--close" aria-hidden="true">
                      &rdquo;
                    </span>
                    <p className="testimonial-card__attribution">
                      &mdash; {item.attribution}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </main>

      <Footer />
      </div>
    </>
  );
}

export default Testimonials;
